import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { FormField } from '../components/FormField';
import { TextInput } from '../components/TextInput';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { useAuth } from '../auth/context';
import { usePostAuthRedirect } from '../org/usePostAuthRedirect';
import { toDisplayError, toFieldErrors } from '../api/errorMessages';
import styles from './SignIn.module.css';

interface FieldErrors {
  email?: string;
  password?: string;
}

interface FormSummary {
  message: string;
  requestId?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Client-side validation. Mirrors backend rules loosely; the server is the
 * source of truth, but empty/format errors are caught here for fast feedback. */
function validate(email: string, password: string): FieldErrors {
  const errors: FieldErrors = {};
  if (!email.trim()) {
    errors.email = 'Enter your email address.';
  } else if (!EMAIL_RE.test(email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (!password) {
    errors.password = 'Enter your password.';
  }
  return errors;
}

export function SignIn() {
  const { login } = useAuth();
  const resolvePostAuth = usePostAuthRedirect();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { sessionExpired?: boolean } };

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [summary, setSummary] = useState<FormSummary | null>(null);
  const [busy, setBusy] = useState(false);

  const summaryRef = useRef<HTMLDivElement>(null);
  const sessionExpired = Boolean(location.state?.sessionExpired);

  // Move focus to the error summary after it commits (deterministic vs rAF,
  // which can lose the race to a re-render under load).
  useEffect(() => {
    if (summary) summaryRef.current?.focus();
  }, [summary]);

  const focusSummary = () => {
    // Defer so the live region has rendered before focus moves to it.
    requestAnimationFrame(() => summaryRef.current?.focus());
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;

    const errors = validate(email, password);
    if (errors.email || errors.password) {
      setFieldErrors(errors);
      setSummary({ message: 'Please fix the errors below and try again.' });
      focusSummary();
      return;
    }

    setFieldErrors({});
    setSummary(null);
    setBusy(true);
    try {
      await login(email, password);
      const dest = await resolvePostAuth();
      navigate(dest, { replace: true });
    } catch (err) {
      // Per-field errors ONLY from a 422 with field discriminators; auth 401s
      // are intentionally form-level (no field detail => no enumeration).
      const fields = toFieldErrors(err);
      if (Object.keys(fields).length > 0) {
        setFieldErrors(fields);
      }
      const display = toDisplayError(err);
      setSummary({
        message: display.message,
        ...(display.requestId ? { requestId: display.requestId } : {}),
      });
      focusSummary();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell variant="centered">
      <Card>
        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <h1 className={styles.heading}>Sign in</h1>
          <p className={styles.subheading}>Access your TestCraft Journeys workspace.</p>

          {/* Form-level error summary. Container always in the DOM so the live
              region announces changes; focus is moved here on submit failure. */}
          <div className={styles.summary}>
            <InlineAlert
              ref={summaryRef}
              severity="error"
              {...(summary?.requestId ? { requestId: summary.requestId } : {})}
            >
              {summary?.message}
            </InlineAlert>
          </div>

          {sessionExpired && !summary ? (
            <div className={styles.summary}>
              <InlineAlert severity="info">
                Your session has expired. Please sign in again.
              </InlineAlert>
            </div>
          ) : null}

          <FormField label="Email" id="email" {...(fieldErrors.email ? { error: fieldErrors.email } : {})}>
            {({ controlProps }) => (
              <TextInput
                {...controlProps}
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </FormField>

          <FormField
            label="Password"
            id="password"
            {...(fieldErrors.password ? { error: fieldErrors.password } : {})}
          >
            {({ controlProps }) => (
              <TextInput
                {...controlProps}
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </FormField>

          <div className={styles.actions}>
            <Button type="submit" busy={busy} fullWidth>
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </div>

          <p className={styles.link}>
            <Link to="/sign-up">New here? Create an account</Link>
          </p>
        </form>
      </Card>
    </AppShell>
  );
}
